/**
 * Native Interactive Site Map (ISM) Modal Handler
 *
 * Pure coordinator: registers the modal with the modal service and manages
 * its open/close event lifecycle. Receives all dependencies as arguments; owns
 * no global state and produces no side effects beyond what is passed in.
 *
 * @param {Object} params
 * @param {Element}  params.appRoot       - The live ISM root element (outerHTML is used as modal payload).
 * @param {Object}   params.modalService  - Modal service instance (setHtml, onHtmlReady, onHtmlRemoved, clearHtml).
 * @param {Function} params.dispatch      - Redux store dispatch.
 * @param {Function} params.modalAction   - Action registering modal (e.g. uiStateSlice.actions.modalInformation).
 * @param {Function} params.onBootstrap   - Async callback invoked with the live modal root once the modal is ready.
 * @param {Function} params.onStop        - Callback invoked when the modal close button is clicked; stops plugin host.
 */
export default function createInteractiveSiteMapModalHandler({
  appRoot,
  modalService,
  dispatch,
  modalAction,
  onBootstrap,
  onStop,
}) {
  const MODAL_ID = 'native-interactive-site-map-modal';
  const modalElement = document.getElementById(MODAL_ID);

  const onCloseButtonClick = async (event) => {
    if (!event.target.closest('[data-close-button]')) {
      return;
    }
    modalService.clearHtml();
    await onStop();
  };

  const bindEvents = () => {    
    if (!modalElement) {
      return;
    }
    modalElement.addEventListener('click', onCloseButtonClick);
  };

  const unbindEvents = () => {
    if (!modalElement || !onCloseButtonClick) {
      return;
    }
    modalElement.removeEventListener('click', onCloseButtonClick);
  };

  dispatch(modalAction({ name: MODAL_ID }));

  modalService.onHtmlReady({
    name: MODAL_ID,
    fn: async () => {
      const modalRoot = modalElement?.querySelector('[data-native-interactive-site-map]');
      if (!modalRoot) {
        return;
      }
      bindEvents();
      await onBootstrap(modalRoot);
    },
  });

  modalService.onHtmlRemoved({
    name: MODAL_ID,
    fn: unbindEvents,
  });
  
  modalService.setHtml({ payload: appRoot.innerHTML });
}
